import { logger } from '../utils/logger';

/**
 * Server-side video generation service
 * Calls our secure server API instead of exposing API keys to the client
 */
export class ServerVideoService {
  private static instance: ServerVideoService;
  
  private constructor() {}
  
  static getInstance(): ServerVideoService {
    if (!ServerVideoService.instance) {
      ServerVideoService.instance = new ServerVideoService();
    }
    return ServerVideoService.instance;
  }

  /**
   * Generate video through server API with model and provider selection
   */
  async generateVideo(script: string, segment?: number, model: string = 'auto', provider: string = 'auto'): Promise<string> {
    logger.info('SERVER_VIDEO_REQUEST', { scriptLength: script.length, segment, model, provider });
    
    try {
      // Call our server API v2 endpoint with model and provider parameters
      const response = await fetch('/api/video/generate-v2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ script, segment, model, provider }),
      });

      const data = await response.json();
      
      if (!response.ok || !data.ok) {
        logger.error('SERVER_VIDEO_ERROR', { 
          status: response.status,
          code: data.code,
          providerStatus: data.providerStatus,
          providerBody: data.providerBody,
          error: data.error,
          reason: data.reason 
        });
        
        // Build detailed error message based on actual provider status
        let errorMessage = '';
        if (data.providerStatus) {
          errorMessage = `Video generation failed (Status ${data.providerStatus}): ${data.error}`;
          
          // Add provider details if available
          if (data.providerBody) {
            logger.warn('PROVIDER_ERROR_DETAILS', data.providerBody);
          }
        } else {
          // Fallback to reason-based messages
          if (data.reason === 'MISSING_API_KEY') {
            errorMessage = 'Video generation service is not configured on the server (API key missing)';
          } else if (data.reason === 'INVALID_API_KEY') {
            errorMessage = 'Server API key is invalid or lacks permissions for Veo model';
          } else if (data.reason === 'MODEL_NOT_FOUND') {
            errorMessage = 'Veo model endpoint not found or not accessible with current API key';
          } else if (data.reason === 'RATE_LIMIT') {
            errorMessage = 'Rate limit exceeded for video generation. Please try again later';
          } else {
            errorMessage = data.error || 'Failed to generate video';
          }
        }
        
        // Create error with full context
        const error = new Error(errorMessage);
        (error as any).reason = data.reason;
        (error as any).providerStatus = data.providerStatus;
        throw error;
      }

      // If we get an operation name, we need to poll for the result
      if (data.operationName) {
        logger.info('SERVER_VIDEO_POLLING_START', { operationName: data.operationName });
        return await this.pollForVideo(data.operationName);
      }

      // If we get a direct URL, return it
      if (data.url) {
        // Check if this is a demo video
        if (data.isDemo) {
          logger.info('SERVER_VIDEO_DEMO_MODE', { 
            url: data.url,
            message: data.message || 'Using demo video'
          });
          console.log('🎬 Demo Mode: ' + (data.message || 'Veo API requires special access, using sample video'));
        } else {
          logger.info('SERVER_VIDEO_SUCCESS', { url: data.url });
        }
        return data.url;
      }

      throw new Error('Unexpected response from server');
      
    } catch (error) {
      logger.error('SERVER_VIDEO_EXCEPTION', { error: error instanceof Error ? error.message : error });
      throw error;
    }
  }

  /**
   * Poll for video generation completion
   */
  private async pollForVideo(operationName: string, maxAttempts: number = 30): Promise<string> {
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      attempts++;
      logger.debug('SERVER_VIDEO_POLL_ATTEMPT', { attempt: attempts, maxAttempts });
      
      // Wait before polling (10 seconds for first attempt, then 5 seconds)
      const delay = attempts === 1 ? 10000 : 5000;
      await new Promise(resolve => setTimeout(resolve, delay));
      
      try {
        const response = await fetch(`/api/video/status?operation=${encodeURIComponent(operationName)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        
        if (!response.ok || !data.ok) {
          logger.error('SERVER_VIDEO_POLL_ERROR', { 
            attempt: attempts,
            error: data.error 
          });
          // Continue polling even if there's an error
          continue;
        }

        if (data.done) {
          if (data.url) {
            logger.info('SERVER_VIDEO_POLL_SUCCESS', { 
              attempts,
              url: data.url 
            });
            return data.url;
          } else {
            throw new Error('Video generation completed but no URL provided');
          }
        }

        // Still processing, continue polling
        logger.debug('SERVER_VIDEO_POLL_IN_PROGRESS', { 
          attempt: attempts,
          metadata: data.metadata 
        });
        
      } catch (error) {
        logger.error('SERVER_VIDEO_POLL_EXCEPTION', { 
          attempt: attempts,
          error: error instanceof Error ? error.message : error 
        });
        // Continue polling even if there's an exception
      }
    }
    
    throw new Error(`Video generation timed out after ${maxAttempts} attempts`);
  }

  /**
   * Generate a fallback storyboard if video generation fails
   */
  generateFallbackStoryboard(script: string): string {
    logger.warn('SERVER_VIDEO_FALLBACK', { scriptLength: script.length });
    
    // Create a simple storyboard data URL as fallback
    const storyboardData = {
      type: 'storyboard',
      script: script,
      frames: script.split('\n').filter(line => line.trim()).slice(0, 4),
      message: 'Video generation unavailable - showing storyboard preview'
    };
    
    // Convert to base64 data URL
    const jsonString = JSON.stringify(storyboardData);
    const base64 = btoa(unescape(encodeURIComponent(jsonString)));
    return `data:video/storyboard;base64,${base64}`;
  }
}

// Export singleton instance
export const serverVideoService = ServerVideoService.getInstance();